/**
 * Starter templates for the Object Editor ("New object → from template").
 * Each one is a plain {@link ObjectDefinitionSpec}. Most are the built-in
 * types' exact data twins (builtins/): repeats with coupled step ↔ count,
 * formulas, copies by formula, linked depth and formula handles; the closet
 * shows alternating mirrored cells and pinned groups.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type ObjectShape, type Vec2 } from 'shared-types';
import { t } from '../../translation/markers';
import { SketchObjectCategory } from '../types';
import { BUILTIN_OBJECT_SPECS } from './builtins';

export interface ObjectDefinitionTemplate {
  id: string;
  label: string;
  description: string;
  /** Category a new object from this template starts in. */
  category: SketchObjectCategory;
  spec: ObjectDefinitionSpec;
}

const line = (id: string, a: Vec2, b: Vec2, extra: Partial<Pick<ObjectShape, 'style' | 'layer' | 'name'>> = {}): ObjectShape => ({ id, kind: 'polyline', points: [a, b], closed: false, ...extra });

// ─── Closet / cabinet runs (wall-attached modules with door swings) ─────

function moduleRun(opts: { width: number; depth: number; height: number; moduleKey: string; moduleLabel: string; countKey: string; countLabel: string; modules: number; rod: boolean; worktop: boolean }): ObjectDefinitionSpec {
  const hw = opts.width / 2;
  const hd = opts.depth / 2;
  const cell = opts.width / opts.modules;
  const shapes: ObjectShape[] = [
    { id: 'carcass', name: 'Carcass', kind: 'rect', min: [-hw, -hd], max: [hw, hd] },
    line('divider', [-hw + cell, -hd], [-hw + cell, hd], { name: 'Divider' }),
    line('leaf', [-hw, hd], [-hw, hd + cell], { name: 'Door leaf' }),
    { id: 'swing', name: 'Door swing', kind: 'arc', center: [-hw, hd], radius: cell, startAngle: Math.PI / 2, endAngle: 0 },
  ];
  if (opts.rod) shapes.push(line('rod', [-hw + 0.05, 0], [hw - 0.05, 0], { name: 'Hanging rod', style: 'dashed' }));
  if (opts.worktop) shapes.push({ id: 'worktop', name: 'Worktop', kind: 'rect', min: [-hw - 0.02, -hd], max: [hw + 0.02, hd + 0.02] });
  return {
    schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
    anchor: 'LINE',
    constraint: { kind: 'WALL', defaultInset: 0 },
    defaultSize: { width: opts.width, depth: opts.depth, height: opts.height },
    params: [
      { key: opts.moduleKey, kind: 'float', label: opts.moduleLabel, default: cell, min: 0.2, max: 1.2, decimals: 2, distance: true },
      { key: opts.countKey, kind: 'int', label: opts.countLabel, default: opts.modules, min: 1 },
      { key: 'doorsOpen', kind: 'boolean', label: 'show door swings', default: false },
      ...(opts.worktop ? [{ key: 'worktop', kind: 'boolean' as const, label: 'worktop', default: true }] : []),
    ],
    shapes,
    groups: [
      {
        id: 'dividers',
        name: 'Dividers',
        shapeIds: ['divider'],
        resize: { u: 'stretch', n: 'stretch' },
        repeat: { kind: 'linear', axis: 'u', cell: [-hw, -hw + cell], pitch: { param: opts.moduleKey }, count: { param: opts.countKey }, fit: 'fill', cellResize: 'stretch', skip: 'last' },
      },
      {
        id: 'doors',
        name: 'Door swings',
        shapeIds: ['leaf', 'swing'],
        // Pinned to the front face so the swings stay attached when the depth changes.
        resize: { u: 'stretch', n: 'pinEnd' },
        repeat: { kind: 'linear', axis: 'u', cell: [-hw, -hw + cell], pitch: { param: opts.moduleKey }, fit: 'fill', cellResize: 'stretch', mirrorAlternate: true },
        visibleWhen: { param: 'doorsOpen', equals: true },
      },
      ...(opts.worktop ? [{ id: 'worktopGroup', name: 'Worktop', shapeIds: ['worktop'], resize: { u: 'stretch' as const, n: 'stretch' as const }, visibleWhen: { param: 'worktop', equals: true } }] : []),
    ],
    handles: { start: [-0.5, 0], end: [0.5, 0] },
    model3d: null,
  };
}

export const OBJECT_DEFINITION_TEMPLATES: readonly ObjectDefinitionTemplate[] = [
  { id: 'stair', label: t('Straight stair'), description: t('Treads follow the run length; step size and number of steps stay in sync'), category: SketchObjectCategory.FIXTURES, spec: BUILTIN_OBJECT_SPECS.stair },
  { id: 'stairL', label: t('L-shaped stair'), description: t('Two flights around a corner landing, steps shared between them'), category: SketchObjectCategory.FIXTURES, spec: BUILTIN_OBJECT_SPECS.stair_l },
  { id: 'stairU', label: t('U-shaped stair'), description: t('Two legs of their own length with a landing; the depth follows the longer leg'), category: SketchObjectCategory.FIXTURES, spec: BUILTIN_OBJECT_SPECS.stair_u },
  { id: 'spiral', label: t('Spiral stair'), description: t('Treads fanned around a newel, any turn angle and direction'), category: SketchObjectCategory.FIXTURES, spec: BUILTIN_OBJECT_SPECS.stair_spiral },
  {
    id: 'closet',
    label: t('Closet'),
    description: t('Wall-attached closet run with doors, dividers and a hanging rod'),
    category: SketchObjectCategory.FURNITURE,
    spec: moduleRun({ width: 1.8, depth: 0.6, height: 2.2, moduleKey: 'doorWidth', moduleLabel: 'door width', countKey: 'doorCount', countLabel: 'number of doors', modules: 3, rod: true, worktop: false }),
  },
  { id: 'cabinets', label: t('Cabinet run'), description: t('Kitchen base cabinets with optional worktop and door swings'), category: SketchObjectCategory.KITCHEN, spec: BUILTIN_OBJECT_SPECS.base_cabinet },
  { id: 'radiator', label: t('Radiator'), description: t('Fins spread evenly over the drawn length'), category: SketchObjectCategory.FIXTURES, spec: BUILTIN_OBJECT_SPECS.radiator },
  { id: 'table', label: t('Table with chairs'), description: t('Rectangular or round; chairs are added as the table grows'), category: SketchObjectCategory.FURNITURE, spec: BUILTIN_OBJECT_SPECS.table },
];
